// users 컬렉션 헬퍼.
// geek1 호환 구조: {uid, displayName, email, level, createdAt} + 2차 개선 신규 {status, roles}.
// 09 §6.10 RBAC 모델 — geek1 retire 전까지 dual-read/dual-write.

import { Timestamp, collection, doc, onSnapshot, query, updateDoc } from 'firebase/firestore';

import { db } from 'firebase/config';

export type UserStatus = 'pending' | 'rejected' | 'active';

// 09 §6.10 — admin / member / viewer (viewer 는 geek1 운영 중엔 부여 비활성, 옵션 b)
export type UserRole = 'admin' | 'member' | 'viewer';

// 알림 임박 시점 — "N일 전부터 당일까지 매일" (카운트다운).
// 'OFF' 면 진행단계 마감 알림 자체 끔.
export type DeadlineLead = 'D-7' | 'D-3' | 'D-1' | 'D-0' | 'OFF';

export interface NotificationPrefs {
  deadlineLead: DeadlineLead;
  stagnantEnabled: boolean;
}

// 정체 사건 알림 default OFF — 마이그레이션 전 옛 사건 다수가 정체로 잡혀
// 노이즈가 되므로. 마이그레이션 후 사용자가 Settings 에서 ON.
export const DEFAULT_NOTIFICATION_PREFS: NotificationPrefs = {
  deadlineLead: 'D-3', // 3일 전부터 당일까지 매일
  stagnantEnabled: false
};

// DeadlineLead → 일 수 (OFF 면 -1)
export function deadlineLeadToDays(lead: DeadlineLead): number {
  switch (lead) {
    case 'D-7':
      return 7;
    case 'D-3':
      return 3;
    case 'D-1':
      return 1;
    case 'D-0':
      return 0;
    case 'OFF':
      return -1;
  }
}

export interface AppUser {
  uid: string;
  displayName: string;
  email: string;
  photoURL?: string; // Google account 의 profile photo URL
  level: number; // geek1 호환 필드 — retire 전까지 유지
  status: UserStatus;
  roles: UserRole[];
  createdAt?: Timestamp;
  lastSignInAt?: Timestamp;
  notificationsReadUntil?: Timestamp;
  notificationPrefs?: NotificationPrefs;
}

const COLLECTION = 'users';

// level → status/roles derive — DB 에 새 필드가 없을 때만 적용
function deriveFromLevel(level: number): { status: UserStatus; roles: UserRole[] } {
  if (level === 9) return { status: 'active', roles: ['admin'] };
  if (level === 2) return { status: 'active', roles: ['member'] };
  if (level === 1) return { status: 'rejected', roles: [] };
  return { status: 'pending', roles: [] };
}

// 새 필드 (status/roles) 가 있으면 그대로 신뢰, 없으면 level 에서 derive
function readUserFromDoc(uid: string, data: Record<string, unknown>): AppUser {
  const level = typeof data.level === 'number' ? data.level : 0;
  const dbStatus = data.status as UserStatus | undefined;
  const dbRoles = Array.isArray(data.roles) ? (data.roles as UserRole[]) : undefined;

  const { status, roles } = dbStatus && dbRoles ? { status: dbStatus, roles: dbRoles } : deriveFromLevel(level);

  return {
    uid,
    displayName: (data.displayName ?? '') as string,
    email: (data.email ?? '') as string,
    photoURL: (data.photoURL as string | undefined) || undefined,
    level,
    status,
    roles,
    createdAt: data.createdAt as Timestamp | undefined,
    lastSignInAt: data.lastSignInAt as Timestamp | undefined,
    notificationsReadUntil: data.notificationsReadUntil as Timestamp | undefined,
    notificationPrefs: data.notificationPrefs as NotificationPrefs | undefined
  };
}

// status + roles → level (geek1 호환 동기화)
// 09 §6.10 dual-write 규칙:
//   pending → 0, rejected → 1, admin 보유 → 9, member 보유 → 2, viewer 보유 → 1 (geek1 차단)
export function rolesToLevel(status: UserStatus, roles: UserRole[]): number {
  if (status === 'pending') return 0;
  if (status === 'rejected') return 1;
  if (roles.includes('admin')) return 9;
  if (roles.includes('member')) return 2;
  if (roles.includes('viewer')) return 1; // 옵션 b 안전망 — geek1 에서 차단됨
  return 0;
}

// 사용자 권한 갱신 — status + roles + level 한 번에 (dual-write).
// admin (level 9) 만 호출 가능 (Firestore Rules 의 `update: level == 9` 가드).
// roles 검증: 'viewer' 는 geek1 retire 전까지 외부에서 부여 못 함 (09 §6.10 옵션 b) — UI 에서 차단.
export async function saveUserRoles(uid: string, status: UserStatus, roles: UserRole[]): Promise<void> {
  const level = rolesToLevel(status, roles);
  await updateDoc(doc(db, COLLECTION, uid), { status, roles, level });
}

// 전체 사용자 실시간 구독 — derive 적용해서 반환
export function subscribeToUsers(onChange: (rows: AppUser[]) => void): () => void {
  return onSnapshot(query(collection(db, COLLECTION)), (snapshot) => {
    const rows: AppUser[] = snapshot.docs.map((d) => readUserFromDoc(d.id, d.data()));
    rows.sort((a, b) => a.displayName.localeCompare(b.displayName));
    onChange(rows);
  });
}

// 본인 사용자 도큐먼트 실시간 구독 (알림 prefs / readUntil 표시용)
export function subscribeToCurrentUser(uid: string, onChange: (user: AppUser | null) => void): () => void {
  return onSnapshot(doc(db, COLLECTION, uid), (snap) => {
    if (!snap.exists()) {
      onChange(null);
      return;
    }
    onChange(readUserFromDoc(snap.id, snap.data()));
  });
}

// 본인의 notificationsReadUntil 갱신 — "모두 읽음" 클릭 시 호출.
// rules: users.update 가 본인 + notificationsReadUntil/notificationPrefs 만 허용.
export async function markNotificationsRead(uid: string, until: Date = new Date()): Promise<void> {
  await updateDoc(doc(db, COLLECTION, uid), { notificationsReadUntil: Timestamp.fromDate(until) });
}

// 본인 알림 설정 저장
export async function saveNotificationPrefs(uid: string, prefs: NotificationPrefs): Promise<void> {
  await updateDoc(doc(db, COLLECTION, uid), { notificationPrefs: prefs });
}
